import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ReceiptText, Search, Check, X, Bot } from "lucide-react";

export default function AdminRefunds({ password }) {
  const [email, setEmail] = useState("");
  const [member, setMember] = useState(null);
  const [txns, setTxns] = useState([]);
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState(null);
  const [requests, setRequests] = useState([]);

  const fetchRequests = useCallback(async () => {
    try {
      const r = await api.get(`/admin/refund-requests?admin_password=${encodeURIComponent(password)}`);
      setRequests(r.data.requests || []);
    } catch (e) {
      console.error("Refund requests fetch failed:", e);
    }
  }, [password]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const processRequest = async (req, action) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/refunds/process?admin_password=${encodeURIComponent(password)}`, {
        user_id: req.user_id, transaction_id: req.transaction_id, action, kind: req.kind || "credit"
      });
      toast.success(action === "approve" ? "Refund approved & processed automatically" : "Refund denied — member notified");
      if (r.data.money_note) toast.info(`Money side: return ${r.data.money_note}`);
      setAiMsg(r.data.ai_message);
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const lookup = async () => {
    if (!email.trim() || busy) return;
    setBusy(true);
    setAiMsg(null);
    try {
      const r = await api.get(`/admin/refunds/history?admin_password=${encodeURIComponent(password)}&email=${encodeURIComponent(email.trim())}`);
      setMember(r.data.member);
      setTxns(r.data.transactions || []);
      setSlots(r.data.slots || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
      setMember(null);
    } finally { setBusy(false); }
  };

  const process = async (kind, id, action) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/refunds/process?admin_password=${encodeURIComponent(password)}`, {
        user_id: member.user_id, transaction_id: id, action, kind
      });
      toast.success(action === "approve" ? "Refund approved & processed automatically" : "Refund denied — member notified");
      if (r.data.money_note) toast.info(`Money side: return ${r.data.money_note}`);
      setAiMsg(r.data.ai_message);
      lookup();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setBusy(false);
    }
  };

  const row = (kind, id, label, sub, status) => (
    <div key={`${kind}-${id}`} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-black/20 border border-white/10" data-testid={`refund-row-${id}`}>
      <div className="min-w-0">
        <p className="text-sm truncate">{label}</p>
        <p className="text-xs text-muted-foreground truncate">{sub}</p>
      </div>
      {status ? (
        <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${status === "approved" ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>
          {status === "approved" ? "Refunded" : "Denied"}
        </span>
      ) : (
        <div className="flex gap-1.5 shrink-0">
          <Button size="sm" className="h-7 px-2 text-xs bg-green-600 hover:bg-green-500" onClick={() => process(kind, id, "approve")} disabled={busy} data-testid={`refund-approve-${id}`}>
            <Check className="w-3 h-3 mr-1" /> Approve
          </Button>
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => process(kind, id, "deny")} disabled={busy} data-testid={`refund-deny-${id}`}>
            <X className="w-3 h-3 mr-1" /> Deny
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="glass-card p-6 mt-8" data-testid="admin-refunds-section">
      <div className="flex items-center gap-3 mb-1">
        <ReceiptText className="w-5 h-5 text-sky-400" />
        <h2 className="text-xl font-semibold">Refunds</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Look up a member, pick a transaction from their history, then Approve or Deny. On approval the AI automatically reverses the account effects, writes a personal message and emails the member. Real-money returns still go through your PayPal dashboard.
      </p>
      {requests.filter((r) => r.status === "pending").length > 0 && (
        <div className="mb-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5" data-testid="member-refund-requests">
          <p className="text-sm font-semibold text-amber-300 mb-3">
            Member requests awaiting review ({requests.filter((r) => r.status === "pending").length})
          </p>
          <div className="space-y-2">
            {requests.filter((r) => r.status === "pending").map((req) => (
              <div key={req.request_id} className="p-3 rounded-lg bg-black/20 border border-white/10" data-testid={`refund-request-${req.request_id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
                  <div className="min-w-0">
                    <p className="text-sm"><b>{req.user_name}</b> <span className="text-muted-foreground text-xs">({req.user_email})</span></p>
                    <p className="text-xs text-white/80 truncate">{req.description}</p>
                    <p className="text-xs text-muted-foreground italic mt-0.5">"{req.reason}"</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" className="h-7 px-2 text-xs bg-green-600 hover:bg-green-500" onClick={() => processRequest(req, "approve")} disabled={busy} data-testid={`request-approve-${req.request_id}`}>
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => processRequest(req, "deny")} disabled={busy} data-testid={`request-deny-${req.request_id}`}>
                      <X className="w-3 h-3 mr-1" /> Deny
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-2 mb-5 max-w-md">
        <Input placeholder="member@email.com" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} data-testid="refund-email-input" />
        <Button onClick={lookup} disabled={busy} data-testid="refund-lookup-btn"><Search className="w-4 h-4 mr-1" /> Look up</Button>
      </div>
      {member && (
        <>
          <p className="text-sm mb-3" data-testid="refund-member-info">
            <b>{member.name}</b> ({member.email}) — current balance <b>{member.credits}</b> credits
          </p>
          {slots.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-muted-foreground mb-2">PREMIUM SLOTS</p>
              <div className="space-y-1.5">
                {slots.map((s) => row(
                  "slot", s.slot_id,
                  `$50 offer slot — ${s.status}`,
                  `${(s.payment_info || {}).method || "paypal"} · ${(s.purchased_at || "").slice(0, 10)}`,
                  s.refund_action
                ))}
              </div>
            </div>
          )}
          <p className="text-xs font-medium text-muted-foreground mb-2">CREDIT TRANSACTIONS</p>
          <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
            {txns.length === 0 ? (
              <p className="text-sm text-muted-foreground">No transactions.</p>
            ) : txns.map((tx) => row(
              "credit", tx.transaction_id || tx.id,
              `${tx.credits > 0 ? "+" : ""}${tx.credits} credits — ${tx.description || tx.type}`,
              `${tx.type} · ${(tx.created_at || "").slice(0, 10)}`,
              tx.refund_action
            ))}
          </div>
          {aiMsg && (
            <div className="mt-4 p-3 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sm" data-testid="refund-ai-message">
              <p className="flex items-center gap-1.5 text-sky-300 font-medium mb-1"><Bot className="w-3.5 h-3.5" /> AI message sent to member:</p>
              <p className="text-white/80 whitespace-pre-wrap">{aiMsg}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
